import { createSlice } from '@reduxjs/toolkit';
import accountingService from '../services/accountingService';
import * as uiSlice from './ui.slice';
import { ProviderServicePricing } from './models/provider-service-pricing.model';
import { PricingFunction, PricingModel } from './models/pricing-model.model';
import { ClientServicePricing } from './models/client-service-pricing.model';
import { TransportationService } from './models/transportation-service.model';
import { TransportationAttribute } from './models/transportation-attribute.model';
import { CommonEntityState } from './models/entity-state';
import { TripLeg } from './models/reservation.model';
import {
  NeedsReviewResponse,
  ProviderInvoiceLineItem,
} from './models/provider-invoice-line-item.model';
import { ProviderInvoicesResponse } from './models/provider-invoice.model';

interface AccountingState {
  pricingFunctions: PricingFunction[];
  loadingPricingFunctions: boolean;
  loadedPricingFunctions: boolean;
  activePricingModels: PricingModel[];
  allPricingModels: PricingModel[];
  loadingPricingModels: boolean;
  loadedPricingModels: boolean;
  savingPricingModel: boolean;
  savedPricingModel: boolean;
  clientServicePricings: ClientServicePricing[];
  conflictingClientServicePricings: ClientServicePricing[] | null;
  loadingClientServicePricings: boolean;
  loadedClientServicePricings: boolean;
  savingClientServicePricing: boolean;
  savedClientServicePricing: boolean;
  transportationServices: TransportationService[];
  loadingTransportationServices: boolean;
  loadedTransportationServices: boolean;
  savingTransportationService: boolean;
  savedTransportationService: boolean;
  transportationAttributes: TransportationAttribute[];
  loadingTransportationAttributes: boolean;
  loadedTransportationAttributes: boolean;
  savingTransportationAttribute: boolean;
  savedTransportationAttribute: boolean;
  loadingProviderServicePricings: boolean;
  loadedProviderServicePricings: boolean;
  savingProviderServicePricing: boolean;
  savedProviderServicePricing: boolean;
  allProviderServicePricings: ProviderServicePricing[];
  estimatedTripLegPricing: { [key: string]: string };
  loadingEstimatedTripLegPricing: boolean;
  loadedEstimatedTripLegPricing: boolean;
  needsReviewData: NeedsReviewResponse | null;
  loadingNeedsReview: boolean;
  loadedNeedsReview: boolean;
  needsReviewParams: {
    page?: number;
    pageSize?: number;
    search?: string;
    groupByProvider?: boolean;
  } | null;
  providerInvoicesData: ProviderInvoicesResponse | null;
  loadingProviderInvoices: boolean;
  loadedProviderInvoices: boolean;
  assigningProviderInvoice: boolean;
  assignedProviderInvoice: boolean;
}

export const initialState: AccountingState = {
  pricingFunctions: [],
  loadingPricingFunctions: false,
  loadedPricingFunctions: false,
  activePricingModels: [],
  allPricingModels: [],
  loadingPricingModels: false,
  loadedPricingModels: false,
  savingPricingModel: false,
  savedPricingModel: false,
  clientServicePricings: [],
  conflictingClientServicePricings: null,
  loadingClientServicePricings: false,
  loadedClientServicePricings: false,
  savingClientServicePricing: false,
  savedClientServicePricing: false,
  transportationServices: [],
  loadingTransportationServices: false,
  loadedTransportationServices: false,
  savingTransportationService: false,
  savedTransportationService: false,
  transportationAttributes: [],
  loadingTransportationAttributes: false,
  loadedTransportationAttributes: false,
  savingTransportationAttribute: false,
  savedTransportationAttribute: false,
  loadingProviderServicePricings: false,
  loadedProviderServicePricings: false,
  savingProviderServicePricing: false,
  savedProviderServicePricing: false,
  allProviderServicePricings: [],
  estimatedTripLegPricing: {},
  loadingEstimatedTripLegPricing: false,
  loadedEstimatedTripLegPricing: false,
  needsReviewData: null,
  loadingNeedsReview: false,
  loadedNeedsReview: false,
  needsReviewParams: null,
  providerInvoicesData: null,
  loadingProviderInvoices: false,
  loadedProviderInvoices: false,
  assigningProviderInvoice: false,
  assignedProviderInvoice: false,
};

export const slice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    setPricingFunctions: (
      state: AccountingState,
      action: { type: string; payload: PricingFunction[] },
    ) => {
      state.pricingFunctions = action.payload;
    },
    setLoadingPricingFunctions: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPricingFunctions = action.payload;
    },
    setLoadedPricingFunctions: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPricingFunctions = action.payload;
    },
    setAllProviderServicePricings: (
      state: AccountingState,
      action: { type: string; payload: ProviderServicePricing[] },
    ) => {
      state.allProviderServicePricings = action.payload;
    },
    setPricingModels: (
      state: AccountingState,
      action: { type: string; payload: PricingModel[] },
    ) => {
      state.allPricingModels = action.payload;
      state.activePricingModels = action.payload.filter(
        (pm) => pm.state === 'active',
      );
    },
    setLoadingPricingModels: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPricingModels = action.payload;
    },
    setLoadedPricingModels: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPricingModels = action.payload;
    },
    setSavingPricingModel: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingPricingModel = action.payload;
    },
    setSavedPricingModel: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedPricingModel = action.payload;
    },
    setLoadingProviderServicePricings: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderServicePricings = action.payload;
    },
    setLoadedProviderServicePricings: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderServicePricings = action.payload;
    },
    setSavingProviderServicePricing: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingProviderServicePricing = action.payload;
    },
    setSavedProviderServicePricing: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedProviderServicePricing = action.payload;
    },
    createPricingModel: (
      state: AccountingState,
      action: {
        type: string;
        payload: PricingModel;
      },
    ) => {
      const allPricingModels = [...state.allPricingModels];
      allPricingModels.push(action.payload);
      allPricingModels.sort((a, b) => a.name.localeCompare(b.name));
      state.allPricingModels = allPricingModels;
      if (action.payload.state === 'active') {
        state.activePricingModels = state.allPricingModels.filter(
          (pm) => pm.state === 'active',
        );
      }
    },
    updatePricingModel: (
      state: AccountingState,
      action: {
        type: string;
        payload: PricingModel;
      },
    ) => {
      const allPricingModels = [...state.allPricingModels];
      const idx = allPricingModels.findIndex(
        (pc) => pc.pricingModelUUID === action.payload.pricingModelUUID,
      );
      if (idx >= 0) {
        allPricingModels.splice(idx, 1, action.payload);
        state.allPricingModels = allPricingModels;
      }
      state.activePricingModels = state.allPricingModels.filter(
        (pm) => pm.state === 'active',
      );
    },
    deletePricingModel: (
      state: AccountingState,
      action: {
        type: string;
        payload: PricingModel;
      },
    ) => {
      const allPricingModels = [...state.allPricingModels];
      const idx = allPricingModels.findIndex(
        (pc) => pc.pricingModelUUID === action.payload.pricingModelUUID,
      );
      if (idx >= 0) {
        allPricingModels.splice(idx, 1);
        state.allPricingModels = allPricingModels;
      }
      state.activePricingModels = state.allPricingModels.filter(
        (pm) => pm.state === 'active',
      );
    },
    setClientServicePricings: (
      state: AccountingState,
      action: { type: string; payload: ClientServicePricing[] },
    ) => {
      state.clientServicePricings = action.payload;
    },
    setLoadingClientServicePricings: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingClientServicePricings = action.payload;
    },
    setLoadedClientServicePricings: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedClientServicePricings = action.payload;
    },
    setSavingClientServicePricing: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingClientServicePricing = action.payload;
    },
    setSavedClientServicePricing: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedClientServicePricing = action.payload;
    },
    createClientServicePricing: (
      state: AccountingState,
      action: {
        type: string;
        payload: ClientServicePricing;
      },
    ) => {
      const clientServicePricings = [...state.clientServicePricings];
      clientServicePricings.push(action.payload);
      clientServicePricings.sort((a, b) => a.name.localeCompare(b.name));
      state.clientServicePricings = clientServicePricings;
    },
    updateClientServicePricing: (
      state: AccountingState,
      action: {
        type: string;
        payload: ClientServicePricing;
      },
    ) => {
      const clientServicePricings = [...state.clientServicePricings];
      const idx = clientServicePricings.findIndex(
        (pc) =>
          pc.clientServicePricingUUID ===
          action.payload.clientServicePricingUUID,
      );
      if (idx >= 0) {
        clientServicePricings.splice(idx, 1, action.payload);
        state.clientServicePricings = clientServicePricings;
      }
    },
    deleteClientServicePricing: (
      state: AccountingState,
      action: {
        type: string;
        payload: ClientServicePricing;
      },
    ) => {
      const clientServicePricings = [...state.clientServicePricings];
      const idx = clientServicePricings.findIndex(
        (pc) =>
          pc.clientServicePricingUUID ===
          action.payload.clientServicePricingUUID,
      );
      if (idx >= 0) {
        clientServicePricings.splice(idx, 1);
        state.clientServicePricings = clientServicePricings;
      }
    },
    setConflictingClientServicePricings: (
      state: AccountingState,
      action: {
        type: string;
        payload: ClientServicePricing[] | null;
      },
    ) => {
      state.conflictingClientServicePricings = action.payload;
    },
    setTransportationServices: (
      state: AccountingState,
      action: { type: string; payload: TransportationService[] },
    ) => {
      state.transportationServices = action.payload;
    },
    setLoadingTransportationServices: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationServices = action.payload;
    },
    setLoadedTransportationServices: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationServices = action.payload;
    },
    setSavingTransportationService: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTransportationService = action.payload;
    },
    setSavedTransportationService: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTransportationService = action.payload;
    },
    createTransportationService: (
      state: AccountingState,
      action: {
        type: string;
        payload: TransportationService;
      },
    ) => {
      const transportationServices = [...state.transportationServices];
      transportationServices.push(action.payload);
      state.transportationServices = transportationServices;
    },
    updateTransportationService: (
      state: AccountingState,
      action: {
        type: string;
        payload: TransportationService;
      },
    ) => {
      const transportationServices = [...state.transportationServices];
      const idx = transportationServices.findIndex(
        (pc) =>
          pc.transportationServiceUUID ===
          action.payload.transportationServiceUUID,
      );
      if (idx >= 0) {
        transportationServices.splice(idx, 1, action.payload);
        state.transportationServices = transportationServices;
      }
    },
    deleteTransportationService: (
      state: AccountingState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      const transportationServices = [...state.transportationServices];
      const idx = transportationServices.findIndex(
        (ts) => ts.transportationServiceUUID === action.payload,
      );
      if (idx >= 0) {
        transportationServices.splice(idx, 1);
        state.transportationServices = transportationServices;
      }
    },
    setTransportationAttributes: (
      state: AccountingState,
      action: { type: string; payload: TransportationAttribute[] },
    ) => {
      state.transportationAttributes = action.payload;
    },
    setLoadingTransportationAttributes: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationAttributes = action.payload;
    },
    setLoadedTransportationAttributes: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationAttributes = action.payload;
    },
    setSavingTransportationAttribute: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTransportationAttribute = action.payload;
    },
    setSavedTransportationAttribute: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTransportationAttribute = action.payload;
    },
    createTransportationAttribute: (
      state: AccountingState,
      action: {
        type: string;
        payload: TransportationAttribute;
      },
    ) => {
      const transportationAttributes = [...state.transportationAttributes];
      transportationAttributes.push(action.payload);
      state.transportationAttributes = transportationAttributes;
    },
    updateTransportationAttribute: (
      state: AccountingState,
      action: {
        type: string;
        payload: TransportationAttribute;
      },
    ) => {
      const transportationAttributes = [...state.transportationAttributes];
      const idx = transportationAttributes.findIndex(
        (pc) =>
          pc.transportationAttributeUUID ===
          action.payload.transportationAttributeUUID,
      );
      if (idx >= 0) {
        transportationAttributes.splice(idx, 1, action.payload);
        state.transportationAttributes = transportationAttributes;
      }
    },
    deleteTransportationAttribute: (
      state: AccountingState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      const transportationAttributes = [...state.transportationAttributes];
      const idx = transportationAttributes.findIndex(
        (pc) => pc.transportationAttributeUUID === action.payload,
      );
      if (idx >= 0) {
        transportationAttributes.splice(idx, 1);
        state.transportationAttributes = transportationAttributes;
      }
    },
    setEstimatedTripLegPricing: (
      state: AccountingState,
      action: { type: string; payload: { [key: string]: string } },
    ) => {
      state.estimatedTripLegPricing = {
        ...state.estimatedTripLegPricing,
        [action.payload.tripLegReferenceID]: action.payload.payableAmount,
      };
    },
    setLoadingEstimatedTripLegPricing: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingEstimatedTripLegPricing = action.payload;
    },
    setLoadedEstimatedTripLegPricing: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedEstimatedTripLegPricing = action.payload;
    },
    setNeedsReviewData: (
      state: AccountingState,
      action: { type: string; payload: NeedsReviewResponse | null },
    ) => {
      state.needsReviewData = action.payload;
    },
    setLoadingNeedsReview: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingNeedsReview = action.payload;
    },
    setLoadedNeedsReview: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedNeedsReview = action.payload;
    },
    setNeedsReviewParams: (
      state: AccountingState,
      action: {
        type: string;
        payload: {
          page?: number;
          pageSize?: number;
          search?: string;
          groupByProvider?: boolean;
        } | null;
      },
    ) => {
      state.needsReviewParams = action.payload;
    },
    setProviderInvoicesData: (
      state: AccountingState,
      action: { type: string; payload: ProviderInvoicesResponse | null },
    ) => {
      state.providerInvoicesData = action.payload;
    },
    setLoadingProviderInvoices: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderInvoices = action.payload;
    },
    setLoadedProviderInvoices: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderInvoices = action.payload;
    },
    setAssigningProviderInvoice: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.assigningProviderInvoice = action.payload;
    },
    setAssignedProviderInvoice: (
      state: AccountingState,
      action: { type: string; payload: boolean },
    ) => {
      state.assignedProviderInvoice = action.payload;
    },
  },
});

export const { setPricingFunctions } = slice.actions;
export const { setLoadingPricingFunctions } = slice.actions;
export const { setLoadedPricingFunctions } = slice.actions;
export const { setAllProviderServicePricings } = slice.actions;
export const { setLoadingProviderServicePricings } = slice.actions;
export const { setLoadedProviderServicePricings } = slice.actions;
export const { setSavingProviderServicePricing } = slice.actions;
export const { setSavedProviderServicePricing } = slice.actions;
export const { setPricingModels } = slice.actions;
export const { setLoadingPricingModels } = slice.actions;
export const { setLoadedPricingModels } = slice.actions;
export const { setSavingPricingModel } = slice.actions;
export const { setSavedPricingModel } = slice.actions;
export const { createPricingModel } = slice.actions;
export const { updatePricingModel } = slice.actions;
export const { deletePricingModel } = slice.actions;
export const { setClientServicePricings } = slice.actions;
export const { setLoadingClientServicePricings } = slice.actions;
export const { setLoadedClientServicePricings } = slice.actions;
export const { setSavingClientServicePricing } = slice.actions;
export const { setSavedClientServicePricing } = slice.actions;
export const { createClientServicePricing } = slice.actions;
export const { updateClientServicePricing } = slice.actions;
export const { deleteClientServicePricing } = slice.actions;
export const { setConflictingClientServicePricings } = slice.actions;
export const { setTransportationServices } = slice.actions;
export const { setLoadingTransportationServices } = slice.actions;
export const { setLoadedTransportationServices } = slice.actions;
export const { setSavingTransportationService } = slice.actions;
export const { setSavedTransportationService } = slice.actions;
export const { createTransportationService } = slice.actions;
export const { updateTransportationService } = slice.actions;
export const { deleteTransportationService } = slice.actions;
export const { setTransportationAttributes } = slice.actions;
export const { setLoadingTransportationAttributes } = slice.actions;
export const { setLoadedTransportationAttributes } = slice.actions;
export const { setSavingTransportationAttribute } = slice.actions;
export const { setSavedTransportationAttribute } = slice.actions;
export const { createTransportationAttribute } = slice.actions;
export const { updateTransportationAttribute } = slice.actions;
export const { deleteTransportationAttribute } = slice.actions;
export const { setEstimatedTripLegPricing } = slice.actions;
export const { setLoadingEstimatedTripLegPricing } = slice.actions;
export const { setLoadedEstimatedTripLegPricing } = slice.actions;
export const { setNeedsReviewData } = slice.actions;
export const { setLoadingNeedsReview } = slice.actions;
export const { setLoadedNeedsReview } = slice.actions;
export const { setNeedsReviewParams } = slice.actions;
export const { setProviderInvoicesData } = slice.actions;
export const { setLoadingProviderInvoices } = slice.actions;
export const { setLoadedProviderInvoices } = slice.actions;
export const { setAssigningProviderInvoice } = slice.actions;
export const { setAssignedProviderInvoice } = slice.actions;

export const fetchPricingFunctionsAsync =
  (states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingPricingFunctions(true));
    dispatch(setLoadedPricingFunctions(false));
    accountingService.getPricingFunctions(states).subscribe({
      next: (pricingFunctions) => {
        dispatch(setPricingFunctions(pricingFunctions));
        dispatch(setLoadingPricingFunctions(false));
        dispatch(setLoadedPricingFunctions(true));
      },
      error: (err) => {
        dispatch(setLoadingPricingFunctions(false));
        dispatch(setLoadedPricingFunctions(false));
      },
    });
  };

export const fetchPricingModelsAsync =
  (states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingPricingModels(true));
    dispatch(setLoadedPricingModels(false));
    accountingService.getPricingModels(states).subscribe({
      next: (pricingModels) => {
        dispatch(setPricingModels(pricingModels));
        dispatch(setLoadingPricingModels(false));
        dispatch(setLoadedPricingModels(true));
      },
      error: (err) => {
        dispatch(setLoadingPricingModels(false));
        dispatch(setLoadedPricingModels(false));
      },
    });
  };

export const fetchProviderServicePricingsAsync =
  (transportationProviderID?: number, states?: CommonEntityState[]) =>
  (dispatch: Function) => {
    dispatch(setLoadingProviderServicePricings(true));
    dispatch(setLoadedProviderServicePricings(false));
    accountingService
      .getProviderServicePricings(transportationProviderID, states)
      .subscribe({
        next: (providerServicePricings) => {
          dispatch(setAllProviderServicePricings(providerServicePricings));
          dispatch(setLoadingProviderServicePricings(false));
          dispatch(setLoadedProviderServicePricings(true));
        },
        error: (err) => {
          dispatch(setLoadingProviderServicePricings(false));
          dispatch(setLoadedProviderServicePricings(false));
        },
      });
  };

export const createPricingModelAsync =
  (pricingModel: PricingModel) => (dispatch: Function) => {
    dispatch(setSavingPricingModel(true));
    dispatch(setSavedPricingModel(false));
    accountingService.createPricingModel(pricingModel).subscribe({
      next: (newPricingModel) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createPricingModelSuccess'),
        );
        dispatch(createPricingModel(newPricingModel));
        dispatch(setSavingPricingModel(false));
        dispatch(setSavedPricingModel(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPricingModel(false));
        dispatch(setSavedPricingModel(false));
      },
    });
  };

export const updatePricingModelAsync =
  (pricingModel: PricingModel) => (dispatch: Function) => {
    dispatch(setSavingPricingModel(true));
    dispatch(setSavedPricingModel(false));
    accountingService.updatePricingModel(pricingModel).subscribe({
      next: (newPricingModel) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updatePricingModelSuccess'),
        );
        dispatch(updatePricingModel(newPricingModel));
        dispatch(setSavingPricingModel(false));
        dispatch(setSavedPricingModel(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPricingModel(false));
        dispatch(setSavedPricingModel(false));
      },
    });
  };

export const deletePricingModelAsync =
  (pricingModel: PricingModel) => (dispatch: Function) => {
    dispatch(setSavingPricingModel(true));
    dispatch(setSavedPricingModel(false));
    accountingService
      .deletePricingModel(pricingModel.pricingModelUUID!)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.deletePricingModelSuccess'),
          );
          dispatch(deletePricingModel(pricingModel));
          dispatch(setSavingPricingModel(false));
          dispatch(setSavedPricingModel(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingPricingModel(false));
          dispatch(setSavedPricingModel(false));
        },
      });
  };

export const updatePricingModelEffectiveDatesAsync =
  (
    pricingModelUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingPricingModel(true));
    dispatch(setSavedPricingModel(false));
    accountingService
      .updatePricingModelEffectiveDates(
        pricingModelUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (newPricingModel) => {
          if (newPricingModel.state === 'active') {
            dispatch(
              uiSlice.setSuccessMessage('snackbar.activatePricingModelSuccess'),
            );
          } else {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.deactivatePricingModelSuccess',
              ),
            );
          }
          dispatch(updatePricingModel(newPricingModel));
          dispatch(setSavingPricingModel(false));
          dispatch(setSavedPricingModel(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingPricingModel(false));
          dispatch(setSavedPricingModel(false));
        },
      });
  };

export const fetchClientServicePricingsAsync =
  (clientBC: string) => (dispatch: Function) => {
    dispatch(setLoadingClientServicePricings(true));
    dispatch(setLoadedClientServicePricings(false));
    accountingService.getClientServicePricings(clientBC).subscribe({
      next: (clientServicePricings) => {
        dispatch(setClientServicePricings(clientServicePricings));
        dispatch(setLoadingClientServicePricings(false));
        dispatch(setLoadedClientServicePricings(true));
      },
      error: (err) => {
        dispatch(setLoadingClientServicePricings(false));
        dispatch(setLoadedClientServicePricings(false));
      },
    });
  };

export const createClientServicePricingAsync =
  (clientServicePricing: ClientServicePricing) =>
  (dispatch: Function): Promise<void> => {
    return new Promise((resolve, reject) => {
      dispatch(setSavingClientServicePricing(true));
      dispatch(setSavedClientServicePricing(false));
      accountingService
        .createClientServicePricing(clientServicePricing)
        .subscribe({
          next: (newClientServicePricing) => {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.createClientServicePricingSuccess',
              ),
            );
            dispatch(createClientServicePricing(newClientServicePricing));
            dispatch(setSavingClientServicePricing(false));
            dispatch(setSavedClientServicePricing(true));
            resolve();
          },
          error: (err) => {
            dispatch(uiSlice.setErrorMessage(err.message));
            dispatch(setSavingClientServicePricing(false));
            dispatch(setSavedClientServicePricing(false));
            reject(err);
          },
        });
    });
  };

export const updateClientServicePricingAsync =
  (clientServicePricing: ClientServicePricing) =>
  (dispatch: Function): Promise<void> => {
    return new Promise((resolve, reject) => {
      dispatch(setSavingClientServicePricing(true));
      dispatch(setSavedClientServicePricing(false));
      accountingService
        .updateClientServicePricing(clientServicePricing)
        .subscribe({
          next: (newClientServicePricing) => {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.updateClientServicePricingSuccess',
              ),
            );
            dispatch(updateClientServicePricing(newClientServicePricing));
            dispatch(setSavingClientServicePricing(false));
            dispatch(setSavedClientServicePricing(true));
            resolve();
          },
          error: (err) => {
            dispatch(uiSlice.setErrorMessage(err.message));
            dispatch(setSavingClientServicePricing(false));
            dispatch(setSavedClientServicePricing(false));
            reject(err);
          },
        });
    });
  };

export const deleteClientServicePricingAsync =
  (clientServicePricing: ClientServicePricing) => (dispatch: Function) => {
    dispatch(setSavingClientServicePricing(true));
    dispatch(setSavedClientServicePricing(false));
    accountingService
      .deleteClientServicePricing(
        clientServicePricing.clientServicePricingUUID!,
      )
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.deleteClientServicePricingSuccess',
            ),
          );
          dispatch(deleteClientServicePricing(clientServicePricing));
          dispatch(setSavingClientServicePricing(false));
          dispatch(setSavedClientServicePricing(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingClientServicePricing(false));
          dispatch(setSavedClientServicePricing(false));
        },
      });
  };

export const testClientServicePricingAsync =
  (clientServicePricing: ClientServicePricing) => (dispatch: Function) => {
    accountingService.testClientServicePricing(clientServicePricing).subscribe({
      next: (conflicts) => {
        dispatch(setConflictingClientServicePricings(conflicts));
      },
    });
  };

export const updateClientServicePricingEffectiveDatesAsync =
  (
    pricingModelUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingClientServicePricing(true));
    dispatch(setSavedClientServicePricing(false));
    accountingService
      .updateClientServicePricingEffectiveDates(
        pricingModelUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (newClientServicePricing) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.activateClientServicePricingSuccess',
            ),
          );
          dispatch(updateClientServicePricing(newClientServicePricing));
          dispatch(setSavingClientServicePricing(false));
          dispatch(setSavedClientServicePricing(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingClientServicePricing(false));
          dispatch(setSavedClientServicePricing(false));
        },
      });
  };

export const fetchTransportationServicesAsync =
  (states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingTransportationServices(true));
    dispatch(setLoadedTransportationServices(false));
    accountingService.getTransportationServices(states).subscribe({
      next: (services) => {
        dispatch(setTransportationServices(services));
        dispatch(setLoadingTransportationServices(false));
        dispatch(setLoadedTransportationServices(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationServices(false));
        dispatch(setLoadedTransportationServices(false));
      },
    });
  };

export const fetchTransportationAttributesAsync =
  (states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingTransportationAttributes(true));
    dispatch(setLoadedTransportationAttributes(false));
    accountingService.getTransportationAttributes(states).subscribe({
      next: (attributes) => {
        // mockAttributeSetAndGroup(attributes);
        dispatch(setTransportationAttributes(attributes));
        dispatch(setLoadingTransportationAttributes(false));
        dispatch(setLoadedTransportationAttributes(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationAttributes(false));
        dispatch(setLoadedTransportationAttributes(false));
      },
    });
  };

export const createTransportationAttributeAsync =
  (transportationAttribute: TransportationAttribute) =>
  (dispatch: Function) => {
    dispatch(setSavingTransportationAttribute(true));
    dispatch(setSavedTransportationAttribute(false));
    accountingService
      .createTransportationAttribute(transportationAttribute)
      .subscribe({
        next: (newTransportationAttribute) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.createTransportationAttributeSuccess',
            ),
          );
          dispatch(createTransportationAttribute(newTransportationAttribute));
          dispatch(setSavingTransportationAttribute(false));
          dispatch(setSavedTransportationAttribute(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationAttribute(false));
          dispatch(setSavedTransportationAttribute(false));
        },
      });
  };

export const updateTransportationAttributeAsync =
  (clientServicePricing: TransportationAttribute) => (dispatch: Function) => {
    dispatch(setSavingTransportationAttribute(true));
    dispatch(setSavedTransportationAttribute(false));
    accountingService
      .updateTransportationAttribute(clientServicePricing)
      .subscribe({
        next: (newTransportationAttribute) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.updateTransportationAttributeSuccess',
            ),
          );
          dispatch(updateTransportationAttribute(newTransportationAttribute));
          dispatch(setSavingTransportationAttribute(false));
          dispatch(setSavedTransportationAttribute(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationAttribute(false));
          dispatch(setSavedTransportationAttribute(false));
        },
      });
  };

export const deleteTransportationAttributeAsync =
  (transportationAttributeUUID: string) => (dispatch: Function) => {
    accountingService
      .deleteTransportationAttribute(transportationAttributeUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.deleteTransportationAttributeSuccess',
            ),
          );
          dispatch(deleteTransportationAttribute(transportationAttributeUUID));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const deleteTransportationServiceAsync =
  (transportationServiceUUID: string) => (dispatch: Function) => {
    dispatch(setSavingTransportationService(true));
    dispatch(setSavedTransportationService(false));
    accountingService
      .deleteTransportationService(transportationServiceUUID!)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.deleteTransportationServiceSuccess',
            ),
          );
          dispatch(deleteTransportationService(transportationServiceUUID));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(false));
        },
      });
  };

export const updateTransportationAttributeEffectiveDatesAsync =
  (
    transportationAttributeUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingTransportationAttribute(true));
    dispatch(setSavedTransportationAttribute(false));
    accountingService
      .updateTransportationAttributeEffectiveDates(
        transportationAttributeUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (newTransportationAttribute) => {
          if (newTransportationAttribute.state === 'active') {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.activateTransportationAttributeSuccess',
              ),
            );
          } else {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.deactivateTransportationAttributeSuccess',
              ),
            );
          }
          dispatch(updateTransportationAttribute(newTransportationAttribute));
          dispatch(setSavingTransportationAttribute(false));
          dispatch(setSavedTransportationAttribute(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationAttribute(false));
          dispatch(setSavedTransportationAttribute(false));
        },
      });
  };

export const createTransportationServiceAsync =
  (transportationService: TransportationService) => (dispatch: Function) => {
    dispatch(setSavingTransportationService(true));
    dispatch(setSavedTransportationService(false));
    accountingService
      .createTransportationService(transportationService)
      .subscribe({
        next: (newTransportationService) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.createTransportationServiceSuccess',
            ),
          );
          dispatch(createTransportationService(newTransportationService));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(false));
        },
      });
  };

export const updateTransportationServiceAsync =
  (clientServicePricing: TransportationService) => (dispatch: Function) => {
    dispatch(setSavingTransportationService(true));
    dispatch(setSavedTransportationService(false));
    accountingService
      .updateTransportationService(clientServicePricing)
      .subscribe({
        next: (newTransportationService) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.updateTransportationServiceSuccess',
            ),
          );
          dispatch(updateTransportationService(newTransportationService));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(false));
        },
      });
  };

export const updateTransportationServiceEffectiveDatesAsync =
  (
    transportationServiceUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingTransportationService(true));
    dispatch(setSavedTransportationService(false));
    accountingService
      .updateTransportationServiceEffectiveDates(
        transportationServiceUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (newTransportationService) => {
          if (newTransportationService.state === CommonEntityState.Active) {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.activateTransportationServiceSuccess',
              ),
            );
          } else {
            dispatch(
              uiSlice.setSuccessMessage(
                'snackbar.deactivateTransportationServiceSuccess',
              ),
            );
          }
          dispatch(updateTransportationService(newTransportationService));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationService(false));
          dispatch(setSavedTransportationService(false));
        },
      });
  };

export const fetchEstimatedTripLegPricingAsync =
  ({
    tripLegReferenceID,
    transportationProviderID,
    passengerUUID,
    coverageAreaID,
    providerTransportationServiceUUID,
  }: {
    tripLegReferenceID: string;
    transportationProviderID: number;
    passengerUUID: string;
    coverageAreaID: number;
    providerTransportationServiceUUID: string;
  }) =>
  (dispatch: Function) => {
    dispatch(setLoadingEstimatedTripLegPricing(true));
    dispatch(setLoadedEstimatedTripLegPricing(false));
    accountingService
      .getEstimatedTripLegPricing({
        tripLegReferenceID,
        transportationProviderID,
        passengerUUID,
        coverageAreaID,
        providerTransportationServiceUUID,
      })
      .subscribe({
        next: (pricing) => {
          dispatch(setEstimatedTripLegPricing(pricing));
          dispatch(setLoadingEstimatedTripLegPricing(false));
          dispatch(setLoadedEstimatedTripLegPricing(true));
        },
        error: (err) => {
          dispatch(
            uiSlice.setErrorMessage(
              'snackbar.estimateTripLegPricingError',
              err.message,
            ),
          );
          dispatch(setLoadingEstimatedTripLegPricing(false));
          dispatch(setLoadedEstimatedTripLegPricing(false));
        },
      });
  };

export const selectPricingFunctions = (state: {
  accounting: AccountingState;
}) => state.accounting.pricingFunctions;
export const selectLoadingPricingFunctions = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingPricingFunctions;
export const selectLoadedPricingFunctions = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedPricingFunctions;
export const selectAllPricingModels = (state: {
  accounting: AccountingState;
}) => state.accounting.allPricingModels;
export const selectActivePricingModels = (state: {
  accounting: AccountingState;
}) => state.accounting.activePricingModels;
export const selectLoadingPricingModels = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingPricingModels;
export const selectLoadedPricingModels = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedPricingModels;
export const selectSavingPricingModel = (state: {
  accounting: AccountingState;
}) => state.accounting.savingPricingModel;
export const selectSavedPricingModel = (state: {
  accounting: AccountingState;
}) => state.accounting.savedPricingModel;
export const selectClientServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.clientServicePricings;
export const selectConflictingClientServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.conflictingClientServicePricings;
export const selectLoadingClientServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingClientServicePricings;
export const selectLoadedClientServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedClientServicePricings;
export const selectSavingClientServicePricing = (state: {
  accounting: AccountingState;
}) => state.accounting.savingClientServicePricing;
export const selectSavedClientServicePricing = (state: {
  accounting: AccountingState;
}) => state.accounting.savedClientServicePricing;
export const selectTransportationServices = (state: {
  accounting: AccountingState;
}) => state.accounting.transportationServices;
export const selectLoadingTransportationServices = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingTransportationServices;
export const selectLoadedTransportationServices = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedTransportationServices;
export const selectSavingTransportationService = (state: {
  accounting: AccountingState;
}) => state.accounting.savingTransportationService;
export const selectSavedTransportationService = (state: {
  accounting: AccountingState;
}) => state.accounting.savedTransportationService;
export const selectTransportationAttributes = (state: {
  accounting: AccountingState;
}) => state.accounting.transportationAttributes;
export const selectLoadingTransportationAttributes = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingTransportationAttributes;
export const selectLoadedTransportationAttributes = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedTransportationAttributes;
export const selectSavingTransportationAttribute = (state: {
  accounting: AccountingState;
}) => state.accounting.savingTransportationAttribute;
export const selectSavedTransportationAttribute = (state: {
  accounting: AccountingState;
}) => state.accounting.savedTransportationAttribute;
export const selectAllProviderServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.allProviderServicePricings;
export const selectLoadingProviderServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingProviderServicePricings;
export const selectLoadedProviderServicePricings = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedProviderServicePricings;
export const selectEstimatedTripLegPricing = (state: {
  accounting: AccountingState;
}) => state.accounting.estimatedTripLegPricing;
export const selectLoadingEstimatedTripLegPricing = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingEstimatedTripLegPricing;
export const selectLoadedEstimatedTripLegPricing = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedEstimatedTripLegPricing;

export const fetchNeedsReviewAsync =
  (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    groupByProvider?: boolean;
  }) =>
  (dispatch: Function) => {
    dispatch(setLoadingNeedsReview(true));
    dispatch(setLoadedNeedsReview(false));
    dispatch(setNeedsReviewParams(params || null));
    accountingService.getNeedsReview(params).subscribe({
      next: (needsReviewData) => {
        dispatch(setNeedsReviewData(needsReviewData));
        dispatch(setLoadingNeedsReview(false));
        dispatch(setLoadedNeedsReview(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setLoadingNeedsReview(false));
        dispatch(setLoadedNeedsReview(false));
      },
    });
  };

export const selectNeedsReviewData = (state: { accounting: AccountingState }) =>
  state.accounting.needsReviewData;
export const selectLoadingNeedsReview = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingNeedsReview;
export const selectLoadedNeedsReview = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedNeedsReview;
export const selectNeedsReviewParams = (state: {
  accounting: AccountingState;
}) => state.accounting.needsReviewParams;

export const fetchProviderInvoicesAsync =
  (params?: { search?: string; status?: string }) => (dispatch: Function) => {
    dispatch(setLoadingProviderInvoices(true));
    dispatch(setLoadedProviderInvoices(false));
    accountingService.fetchProviderInvoices(params).subscribe({
      next: (providerInvoicesData) => {
        dispatch(setProviderInvoicesData(providerInvoicesData));
        dispatch(setLoadingProviderInvoices(false));
        dispatch(setLoadedProviderInvoices(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setLoadingProviderInvoices(false));
        dispatch(setLoadedProviderInvoices(false));
      },
    });
  };

export const assignProviderInvoiceAsync =
  (providerInvoiceID: number, userAccountID: number | null) =>
  (dispatch: Function) => {
    dispatch(setAssigningProviderInvoice(true));
    dispatch(setAssignedProviderInvoice(false));
    accountingService
      .assignProviderInvoice(providerInvoiceID, userAccountID)
      .subscribe({
        next: () => {
          dispatch(setAssigningProviderInvoice(false));
          dispatch(setAssignedProviderInvoice(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setAssigningProviderInvoice(false));
          dispatch(setAssignedProviderInvoice(false));
        },
      });
  };

export const selectProviderInvoicesData = (state: {
  accounting: AccountingState;
}) => state.accounting.providerInvoicesData;
export const selectLoadingProviderInvoices = (state: {
  accounting: AccountingState;
}) => state.accounting.loadingProviderInvoices;
export const selectLoadedProviderInvoices = (state: {
  accounting: AccountingState;
}) => state.accounting.loadedProviderInvoices;
export const selectAssigningProviderInvoice = (state: {
  accounting: AccountingState;
}) => state.accounting.assigningProviderInvoice;
export const selectAssignedProviderInvoice = (state: {
  accounting: AccountingState;
}) => state.accounting.assignedProviderInvoice;

export default slice.reducer;
